import React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { actions } from '../Users/redux/actions';
import Strings from '../../../Strings';

function OrganizationForm(props) {
  const { match, getOrganizationDetails, getOrganizationFormBundle, getOrgGroups, createOrganization, editOrganization, onNavigate } = props;
  const orgId = match?.params?.orgId;
  const isEditMode = orgId && orgId !== 'new';

  const [formData, setFormData] = React.useState({
    name: '',
    org_group_uuid: '',
    business_state: '',
  });
  const [orgGroups, setOrgGroups] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [validationErrors, setValidationErrors] = React.useState({});

  // Load dropdown data
  React.useEffect(() => {
    const loadDropdownData = async () => {
      try {
        // Load org groups
        if (getOrgGroups) {
          const orgGroupsResp = await getOrgGroups({ limit: 1000, offset: 0 });
          const groupsData = Array.isArray(orgGroupsResp?.response)
            ? orgGroupsResp.response
            : orgGroupsResp?.response?.data || [];
          setOrgGroups(groupsData);
        }


      } catch (err) {
        console.error('Failed to load dropdown data:', err);
      }
    };

    loadDropdownData();
  }, [getOrgGroups, getOrganizationFormBundle]);

  // Load organization data if editing
  React.useEffect(() => {
    if (isEditMode && getOrganizationDetails) {
      setIsLoading(true);
      setError(null);

      getOrganizationDetails(orgId)
        .then(resp => {
          const data = resp?.response;
          if (data) {
            setFormData({
              name: data.name || '',
              org_group_uuid: data.org_group_uuid || '',
              business_state: data.business_state || '',
            });
          }
          setIsLoading(false);
        })
        .catch(err => {
          console.error('Failed to load organization:', err);
          setError('Failed to load organization');
          setIsLoading(false);
        });
    }
  }, [isEditMode, orgId, getOrganizationDetails]);

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = Strings.nameRequired || 'Name is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setValidationErrors(prev => ({
      ...prev,
      [name]: '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const data = {
        name: formData.name,
        org_group_uuid: formData.org_group_uuid || undefined,
        business_state: formData.business_state || undefined,
      };

      // Remove undefined values
      Object.keys(data).forEach(key => {
        if (data[key] === undefined) {
          delete data[key];
        }
      });

      if (isEditMode) {
        await editOrganization(orgId, data);
      } else {
        await createOrganization(data);
      }

      onNavigate('/admin/organizations');
    } catch (err) {
      console.error('Failed to save organization:', err);
      const errorMessage = err?.response?.data?.error_msg || 'Failed to save organization';
      setError(errorMessage);
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onNavigate('/admin/organizations');
  };

  if (isLoading) {
    return (
      <div className="organization-form-page" style={{ padding: '20px' }}>
        <div className="loading">{Strings.loading || 'Loading...'}</div>
      </div>
    );
  }

  return (
    <div className="organization-form-page" style={{ padding: '20px' }}>
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <h1>{isEditMode ? (Strings.editOrganization || 'Edit Organization') : (Strings.createOrganization || 'Create Organization')}</h1>
      </div>

      <div className="form-container" style={{ maxWidth: '600px' }}>
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="error-message" style={{ color: 'red', marginBottom: '20px', padding: '10px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
              <p>{error}</p>
            </div>
          )}

          <div className="form-section" style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '15px' }}>{Strings.basicInformation || 'Basic Information'}</h3>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label htmlFor="name" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                {Strings.name || 'Name'} <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={isSaving}
                placeholder="Organization name"
                style={{ width: '100%', padding: '8px', border: validationErrors.name ? '1px solid red' : '1px solid #ccc', borderRadius: '4px', color: '#000' }}
              />
              {validationErrors.name && <span style={{ color: 'red', fontSize: '12px' }}>{validationErrors.name}</span>}
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label htmlFor="org_group_uuid" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                {Strings.orgGroup || 'Org Group'}
              </label>
              <select
                id="org_group_uuid"
                name="org_group_uuid"
                value={formData.org_group_uuid}
                onChange={handleInputChange}
                disabled={isSaving}
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', color: '#000' }}
              >
                <option value="">{Strings.selectOrgGroup || 'Select Org Group'}</option>
                {orgGroups.map(group => (
                  <option key={group.uuid || group.id} value={group.uuid}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label htmlFor="business_state" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                {Strings.businessState || 'Business State'}
              </label>
              <select
                id="business_state"
                name="business_state"
                value={formData.business_state}
                onChange={handleInputChange}
                disabled={isSaving}
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', color: '#000' }}
              >
                <option value="">{Strings.selectBusinessState || 'Select Business State'}</option>
                <option value="created">Created</option>
                <option value="proof_of_service">Proof of Service</option>
                <option value="launched">Launched</option>
                <option value="disengaged">Disengaged</option>
              </select>
            </div>
          </div>

          <div className="form-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn-cancel"
              onClick={handleCancel}
              disabled={isSaving}
              style={{ padding: '10px 20px', cursor: 'pointer', backgroundColor: '#f5f5f5', border: '1px solid #ccc', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
            >
              {Strings.cancel || 'Cancel'}
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={isSaving}
              style={{ padding: '10px 20px', cursor: 'pointer', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
            >
              {isSaving ? (Strings.saving || 'Saving...') : (isEditMode ? (Strings.update || 'Update') : (Strings.create || 'Create'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const mapDispatchToProps = dispatch => ({
  getOrganizationDetails: orgId => dispatch(actions.getOrganizationDetails(orgId)),
  getOrganizationFormBundle: () => dispatch(actions.getOrganizationFormBundle()),
  getOrgGroups: params => dispatch(actions.getOrgGroups(params)),
  createOrganization: data => dispatch(actions.createOrganization(data)),
  editOrganization: (orgId, data) => dispatch(actions.editOrganization(orgId, data)),
  onNavigate: path => dispatch(push(path)),
});

export default connect(null, mapDispatchToProps)(OrganizationForm);
